// Phone layout for the Postbox (scanned post). State and handlers come from MailInbox via `ctx`.
import { CheckCircle2, CornerUpRight, FolderCheck, Loader2, RefreshCw, ShieldAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { PullToRefresh } from "@/components/phone/PhoneBits";
import { PhoneEmailCard, PhoneFolderChips, PhoneInboxHeader, PhoneThreadView, SwipeRow, ThreadBarButton } from "@/pages/inbox/phone/PhoneInboxBits";
import { legalMailMoveDone } from "@/lib/api";
import { cn } from "@/lib/utils";

export function PhonePostboxView({ ctx }) {
  const { data, loading, err, tab, setTab, admin, emails, untriaged, selected, setSelectedId, refresh, heldList, autopilotButton,
    openAssign, openForward, readingPane, typeTone, regardingLabel, lastSync } = ctx;

  const swipeDone = async (email) => {
    try {
      await legalMailMoveDone(email.id);
      setSelectedId(null);
      toast.success("Moved to Done");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const tabs = [
    { id: "inbox", label: "Inbox", count: tab === "inbox" ? emails.length : undefined },
    { id: "done", label: "Done" },
    ...(admin ? [{ id: "held", label: "Held" }] : []),
  ];

  const badges = (e) => {
    const t = e.triage;
    const reg = regardingLabel(t);
    return (
      <>
        {t ? (
          <>
            <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", typeTone[t.letter_type] || typeTone.Other)}>{t.letter_type}</Badge>
            {reg && <span className="text-[11px] text-slate-500 truncate max-w-[180px]">{reg}</span>}
          </>
        ) : <span className="text-[11px] text-slate-400 italic">awaiting triage…</span>}
        {(e.assigned || []).map((a) => (
          <span key={a.case_id} className="inline-flex items-center gap-0.5 min-w-0 max-w-[160px] text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0">
            <FolderCheck className="w-2.5 h-2.5 shrink-0" /><span className="truncate">{a.customer_name || "filed"}</span>
          </span>
        ))}
      </>
    );
  };

  return (
    <div className="-m-3 min-h-dvh bg-slate-50 pb-24" data-testid="phone-inbox-postbox">
      <PhoneInboxHeader title="Postbox" mailbox={data?.mailbox || "scanned post"} count={lastSync ? `Updated ${lastSync.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : ""}>
        {admin && <div data-testid="phone-postbox-autopilot">{autopilotButton}</div>}
        <button type="button" onClick={refresh} disabled={loading} className="h-10 w-10 flex items-center justify-center rounded-lg text-slate-600 sf-tap disabled:opacity-50" aria-label="Pull mail" data-testid="phone-inbox-refresh">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
        </button>
      </PhoneInboxHeader>
      <PhoneFolderChips tabs={tabs} active={tab} onChange={setTab} />
      {tab === "inbox" && untriaged > 0 && <div className="px-3 py-1.5 text-[12px] text-violet-700 bg-violet-50 border-b inline-flex w-full items-center gap-1"><Sparkles className="w-3.5 h-3.5" />{untriaged} awaiting AI triage</div>}
      {err && <div className="m-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="phone-inbox-error">{String(err)}</div>}

      {tab === "held" ? (
        <div className="bg-white border-b" data-testid="phone-postbox-held">
          <div className="px-3 py-2 text-[12px] text-amber-800 bg-amber-50 inline-flex w-full items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" />Scans the privacy autopilot is holding for you</div>
          {heldList}
        </div>
      ) : (
        <PullToRefresh onRefresh={refresh}>
          <div className="bg-white divide-y border-b" data-testid="phone-inbox-list">
            {loading && emails.length === 0 && <div className="p-8 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading post…</div>}
            {!loading && emails.length === 0 && <div className="p-8 text-center text-sm text-slate-500" data-testid="phone-inbox-empty">{tab === "inbox" ? "No post in the inbox — all caught up." : "Nothing in Done yet."}</div>}
            {emails.map((e) => (
              <SwipeRow key={e.id} testid={`phone-email-row-${e.id}`}
                right={tab !== "done" ? { label: "Done", icon: <CheckCircle2 className="w-4 h-4" />, onTrigger: () => swipeDone(e) } : null}
                left={{ label: "Assign to case", icon: <FolderCheck className="w-4 h-4" />, onTrigger: () => openAssign(e) }}>
                <PhoneEmailCard email={e} badges={badges(e)} selectMode={false} checked={false} onOpen={setSelectedId} onToggle={() => {}} />
              </SwipeRow>
            ))}
          </div>
        </PullToRefresh>
      )}

      {selected && (
        <PhoneThreadView title={selected.subject || "(no subject)"} subtitle={`${selected.from_name || selected.from_email || ""} · Postbox`} onBack={() => setSelectedId(null)}
          bottomBar={(
            <div className="flex items-stretch">
              <ThreadBarButton icon={FolderCheck} label="Assign to case" primary onClick={() => openAssign(selected)} testid="phone-thread-assign" />
              <ThreadBarButton icon={CornerUpRight} label="Forward" onClick={() => openForward(selected)} testid="phone-thread-forward" />
              {tab !== "done" && <ThreadBarButton icon={CheckCircle2} label="Done" onClick={() => swipeDone(selected)} testid="phone-thread-done" />}
            </div>
          )}>
          {readingPane}
        </PhoneThreadView>
      )}
    </div>
  );
}
